import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { EventAuthService } from './event-auth.service';
import { EventconfigStoreService } from './eventconfig-store.service';
import { PlayerStoreService } from './player-store.service';
import { groupPolls, itemPolls, MyVote, PollCounts, PollItem, PollsState, ViewerPoll, VodPoll, VoteOutcome, VotePayload } from './poll';

/**
 * Umfragen des Events für die Zuschauerseite.
 *
 * Der Socket /polls liefert beim Verbinden den aktuellen Stand und danach jede Änderung:
 * polls_state, wenn eine Umfrage für Zuschauer auf- oder zugeht oder ihr Ergebnis
 * erscheint (schon synchron zum Stream verzögert), polls_counts für die Zählerstände.
 *
 * Abgestimmt wird per HTTP. Wer abstimmt, entscheidet der Server: bei Login die Person,
 * sonst dieser Browser (clientId im localStorage). Die eigenen Stimmen holt
 * loadMyVotes() nach jedem (Wieder-)Verbinden, damit ein Reload sie nicht vergisst.
 *
 * Die Verbindung zählt ihre Nutzer: Desktop- und Mobil-Ansicht binden die Komponente an
 * verschiedenen Stellen ein, verbunden wird nur einmal.
 *
 * Nach dem Event mit Aufzeichnung (enterVod) kommen die Umfragen nicht mehr live, sondern
 * aus GET /polls/vod: Jede erscheint an der Stelle im Video, an der sie gestellt wurde, ihr
 * Ergebnis ab der Stelle, an der es besprochen wurde — nach der Abspielposition des Players.
 */
@Injectable({
  providedIn: 'root'
})
export class PollStoreService {
  private readonly http = inject(HttpClient);
  private readonly es = inject(EventconfigStoreService);
  private readonly eventAuth = inject(EventAuthService);
  private readonly playerStore = inject(PlayerStoreService);

  private readonly nodeServiceUrl = environment.production ? 'event-backend' : 'test-event-backend';
  private readonly api = `${environment.apiUrl}v1/${this.nodeServiceUrl}/polls`;

  /** Stand vom Socket (vor und während des Events) */
  private readonly livePolls = signal<ViewerPoll[]>([]);

  /** VoD: Umfragen mit ihren Stellen im Video; null = live */
  private readonly vodPolls = signal<VodPoll[] | null>(null);
  readonly vodMode = computed(() => this.vodPolls() !== null);
  readonly vodAvailable = computed(() => (this.vodPolls()?.length ?? 0) > 0);
  /** Abspielposition, vom Player jede Sekunde */
  private readonly playTime = toSignal(this.playerStore.currentTime$, { initialValue: 0 });
  /** Gleiche Zuschauersicht für gleichen Stand, damit nicht jede Sekunde alles neu entsteht */
  private readonly vodViews = new Map<string, ViewerPoll>();
  private vodLoading = false;

  /** Was die Ansicht zeigt: live der Stand vom Socket, im VoD die bis zur Abspielposition gestellten */
  readonly polls = computed(() => {
    const vod = this.vodPolls();
    return vod ? this.vodView(vod) : this.livePolls();
  }, { equal: (a, b) => a.length === b.length && a.every((poll, i) => poll === b[i]) });
  /** In der CCP eingeschaltet (features.strawpolls); kommt live über den Socket */
  readonly enabled = signal(false);
  /** pollId → eigene Stimme (Auswahl, freie Antwort oder übersprungen) */
  readonly myVotes = signal<Record<string, MyVote>>({});

  /**
   * Eine offene Umfrage, bei der hier noch nicht abgestimmt wurde — für das „Neu"-Abzeichen.
   * Es können mehrere gleichzeitig offen sein (Einstellung in der CCP); dann die jüngste.
   */
  readonly firstUnvotedOpenPoll = computed(() =>
    this.polls().find((poll) => poll.status === 'open' && !this.myVotes()[poll.id]) ?? null
  );
  readonly hasUnvotedOpenPoll = computed(() => !!this.firstUnvotedOpenPoll());

  /** Einträge der Ansicht: einzelne Umfragen und Ketten (eine Kette = ein Eintrag) */
  readonly items = computed(() => groupPolls(this.polls()));

  /**
   * Gewählte Umfrage. Liegt hier und nicht in der Komponente, weil auf dem Desktop die Tabs
   * im Header der Seite stehen und die Umfrage selbst in der rechten Spalte. null = der
   * vorderste (offene bzw. jüngste) Eintrag. In einer Kette ist es die Frage im Blick.
   */
  private readonly selectedId = signal<string | null>(null);

  readonly displayedItem = computed((): PollItem | null => {
    const items = this.items();
    const selected = this.selectedId();
    return items.find((item) => itemPolls(item).some((poll) => poll.id === selected)) ?? items[0] ?? null;
  });

  /**
   * Frage im Blick: die gewählte, sonst in einer Kette die erste offene Frage, bei der hier
   * noch nicht abgestimmt wurde (bzw. die offene), sonst die vorderste.
   */
  readonly displayedPoll = computed((): ViewerPoll | null => {
    const item = this.displayedItem();
    if (!item) return null;
    const polls = itemPolls(item);
    const selected = polls.find((poll) => poll.id === this.selectedId());
    if (selected) return selected;
    const open = polls.filter((poll) => poll.status === 'open');
    return open.find((poll) => !this.myVotes()[poll.id]) ?? open[0] ?? polls[0] ?? null;
  });

  selectPoll(id: string): void {
    this.selectedId.set(id);
  }

  /** Eintrag wählen; bei einer Kette landet man bei der offenen Frage, die noch Antwort braucht */
  selectItem(item: PollItem): void {
    const polls = itemPolls(item);
    const open = polls.filter((poll) => poll.status === 'open');
    const target = open.find((poll) => !this.myVotes()[poll.id]) ?? open[0] ?? polls[0];
    this.selectedId.set(target?.id ?? null);
  }

  private socket: Socket | null = null;
  private users = 0;
  private fallbackClientId: string | null = null;
  /** VoD: bisher erreichte Umfragen — eine neue wird gewählt, wie live eine neu geöffnete */
  private vodReached: Set<string> | null = null;

  constructor() {
    effect(() => {
      const ids = this.polls().map((poll) => poll.id);
      if (!this.vodMode()) {
        this.vodReached = null;
        return;
      }
      const reached = this.vodReached;
      this.vodReached = new Set(ids);
      // Neueste zuerst: beim Vorspulen über mehrere hinweg landet man bei der letzten
      const fresh = reached ? ids.find((id) => !reached.has(id)) : undefined;
      if (fresh) untracked(() => this.selectedId.set(fresh));
    });
  }

  /** Nach dem Event mit Aufzeichnung: Umfragen an ihren Stellen im Video statt live */
  async enterVod(): Promise<void> {
    if (this.vodLoading || this.vodPolls() !== null) return;
    this.vodLoading = true;
    try {
      const response = await firstValueFrom(this.http.get<{ polls: VodPoll[] }>(`${this.api}/vod`));
      this.vodViews.clear();
      this.vodPolls.set(response?.polls ?? []);
    } catch (error) {
      // Beim nächsten Status-Abruf wieder versucht
      console.error('Umfragen fürs VoD konnten nicht geladen werden', error);
    } finally {
      this.vodLoading = false;
    }
  }

  leaveVod(): void {
    if (this.vodPolls() === null) return;
    this.vodPolls.set(null);
    this.vodViews.clear();
  }

  /** Bis zur Abspielposition gestellte Umfragen, zuletzt gestellte zuerst; Ergebnis ab seiner Stelle */
  private vodView(vod: VodPoll[]): ViewerPoll[] {
    const time = this.playTime();
    const lang = this.es.activeLang();
    const reached: { poll: ViewerPoll; at: number }[] = [];
    for (const entry of vod) {
      const questionAt = vodTime(entry.questionAt, lang);
      if (questionAt === null || questionAt > time) continue;
      const resultAt = vodTime(entry.resultAt, lang);
      const shown = resultAt !== null && resultAt <= time;
      const key = `${entry.id}:${resultAt !== null}:${shown}`;
      let poll = this.vodViews.get(key);
      if (!poll) {
        poll = vodViewerPoll(entry, resultAt !== null, shown);
        this.vodViews.set(key, poll);
      }
      reached.push({ poll, at: questionAt });
    }
    return reached.sort((a, b) => b.at - a.at).map((entry) => entry.poll);
  }

  connect(): void {
    if (this.users++ > 0) return;

    this.socket = io(environment.apiUrl + 'polls', {
      path: `/v1/${this.nodeServiceUrl}/socket/`,
      auth: {
        token: this.es.getInitCipher(),
        customer: environment.customerShort,
        instance: 'event'
      }
    });

    this.socket.on('connect', () => this.loadMyVotes());
    this.socket.on('polls_state', (state: PollsState) => this.applyState(state));
    this.socket.on('polls_counts', (counts: PollCounts) => this.applyCounts(counts));
  }

  disconnect(): void {
    if (--this.users > 0) return;
    this.users = 0;
    this.socket?.disconnect();
    this.socket = null;
  }

  /** Abstimmen, frei antworten oder überspringen ({ skip: true } — wird mitgezählt) */
  async vote(pollId: string, payload: VotePayload): Promise<VoteOutcome> {
    // Sofort als abgestimmt zeigen; bei einem Fehler wird es zurückgenommen.
    const mine = toMyVote(payload);
    this.setMyVote(pollId, mine);

    try {
      await firstValueFrom(this.http.post(`${this.api}/${pollId}/vote`, { ...payload, clientId: this.clientId() }));
      return 'ok';
    } catch (error) {
      const response = error as HttpErrorResponse;
      const code = response.error?.code;

      // Schon abgestimmt (z. B. in einem anderen Tab): die damalige Stimme anzeigen.
      if (response.status === 409 && code === 'already_voted') {
        this.setMyVote(pollId, response.error?.vote ?? { optionIds: response.error?.optionIds ?? mine.optionIds });
        return 'ok';
      }

      this.setMyVote(pollId, null);
      if (response.status === 409 && code === 'poll_closed') return 'closed';
      if (response.status === 401) return 'login';
      return 'error';
    }
  }

  private async loadMyVotes(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<{ votes: ({ pollId: string } & MyVote)[] }>(`${this.api}/my-votes`, { clientId: this.clientId() })
      );
      const votes: Record<string, MyVote> = {};
      for (const { pollId, ...vote } of response?.votes ?? []) votes[pollId] = vote;
      this.myVotes.set(votes);
    } catch (error) {
      // Ohne die eigenen Stimmen kann trotzdem abgestimmt werden; ein Doppelversuch
      // beantwortet der Server mit already_voted und der damaligen Wahl.
      console.error('Eigene Stimmen konnten nicht geladen werden', error);
    }
  }

  private applyState(state: PollsState): void {
    this.enabled.set(state?.enabled === true);
    const previousOpen = new Set(this.livePolls().filter((poll) => poll.status === 'open').map((poll) => poll.id));
    const polls = state?.polls ?? [];
    this.livePolls.set(polls);

    // Neu geöffnete Umfrage → dorthin springen, auch wenn gerade eine ältere gewählt ist.
    // In einer Kette ist das die nächste Frage; bei „alle Fragen sofort" die erste davon.
    const opened = polls.find((poll) => poll.status === 'open' && !previousOpen.has(poll.id));
    if (opened) this.selectedId.set(opened.series?.mode === 'all' ? null : opened.id);
  }

  private applyCounts(counts: PollCounts): void {
    const byId = new Map((counts?.polls ?? []).map((entry) => [entry.id, entry]));
    if (!byId.size) return;
    this.livePolls.update((polls) => polls.map((poll) => {
      const entry = byId.get(poll.id);
      return entry && poll.resultsVisible
        ? { ...poll, results: { counts: entry.counts, voterCount: entry.voterCount } }
        : poll;
    }));
  }

  private setMyVote(pollId: string, vote: MyVote | null): void {
    this.myVotes.update((votes) => {
      const next = { ...votes };
      if (vote) next[pollId] = vote;
      else delete next[pollId];
      return next;
    });
  }

  /**
   * Dieselbe Client-ID wie für die Zählung anonymer Zugriffe. Ohne localStorage
   * (Privatmodus) eine flüchtige ID für diese Seite — dann ist nach einem Reload eine
   * zweite Stimme möglich, was bei offenen Events ohnehin nicht zu verhindern ist.
   */
  private clientId(): string {
    const stored = this.eventAuth.getOrCreateClientId(this.es.token);
    if (stored) return stored;
    this.fallbackClientId ??= crypto.randomUUID();
    return this.fallbackClientId;
  }
}

/** Stelle im Video in der Sprache des Zuschauers, sonst die erste vorhandene */
function vodTime(times: Record<string, number> | null, lang: string): number | null {
  if (!times) return null;
  const value = times[lang] ?? Object.values(times)[0];
  return typeof value === 'number' ? value : null;
}

/** VoD-Umfrage als beendete Umfrage; ihr Ergebnis erscheint wie „nach Veröffentlichung" erst an seiner Stelle */
function vodViewerPoll(entry: VodPoll, hasResult: boolean, shown: boolean): ViewerPoll {
  const { questionAt, resultAt, results, ...content } = entry;
  return {
    ...content,
    status: 'closed',
    resultsVisibility: hasResult ? 'onPublish' : 'never',
    resultsVisible: shown,
    resultsPending: hasResult && !shown,
    results: shown ? results : null,
    openedAt: 0
  };
}

function toMyVote(payload: VotePayload): MyVote {
  if ('skip' in payload) return { optionIds: [], skipped: true };
  if ('text' in payload) return { optionIds: [], text: payload.text };
  return { optionIds: payload.optionIds };
}
